import styled from "styled-components"
import HeaderComponent from "../Common/Header"

import imgApartment from "@assets/img/apartment.jpg"
import adminServices from "@assets/img/admin-services.jpg"
import maintenance from "@assets/img/maintenance.jpg"
import customerService from "@assets/img/customer-service.jpg"

import Footer from "../Common/Footer"
import { GridRow } from "../../../components/layout/GridRow"
import GoogleReviews from "@features/GoogleReviews"

// Common Styled Components
const PageWrapper = styled.div`
  background: #f6f8fa;
  color: ${({ theme }) => theme.textPrimary};
  line-height: 1.8;
`

const Section = styled.section`
  max-width: ${({ $maxWidth }) => $maxWidth || "1280px"};
  width: min(92vw, ${({ $maxWidth }) => $maxWidth || "1280px"});
  background: ${({ background }) => background || "transparent"};
  margin: clamp(4rem, 8vw, 7rem) auto;
  padding: clamp(2rem, 4vw, 3.5rem);
  text-align: left;
  & + & {
    /* border-top: 1px solid ${({ theme }) => theme.borderColor}; */
    padding-top: 3rem;
  }

  h3 {
    font-size: 1.35rem;
    margin-bottom: 1rem;
    margin-top: 0;
    color: ${({ theme }) => theme.primaryDark};
  }

  @media screen and (max-width: 768px) {
    padding: 0.8rem;
    margin: 1rem auto;
  }
`

const SectionTitle = styled.h2`
  font-size: clamp(2.5rem, 5vw, 5rem);
  line-height: 1;
  letter-spacing: -0.05em;
  font-weight: 560;
  color: ${({ theme }) => theme.primaryDark};
  margin-bottom: 1rem;

  @media screen and (max-width: 768px) {
    font-size: 2.2rem;
  }
`

const SectionDescription = styled.p`
  font-size: 1.12rem;
  max-width: ${({ $maxWidth }) => $maxWidth || "800px"};
  margin: 1rem 0 3rem;
  color: ${({ theme }) => theme.textSecondary};
`

// Specific Section Styles
const HeroSection = styled.section`
  background: url("/img/modern-apartment.jpg") no-repeat center center/cover;
  color: ${({ theme }) => theme.white};
  min-height: 650px;
  display: flex;
  align-items: center;
  gap: clamp(2rem, 5vw, 5rem);
  text-align: left;
  padding: clamp(6rem, 10vw, 9rem) max(4vw, calc((100vw - 1280px) / 2));
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, rgba(5, 24, 42, 0.95) 0%, rgba(6, 43, 75, 0.8) 52%, rgba(7, 39, 68, 0.18) 100%);
    z-index: 1;
  }

  > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 900px) {
    min-height: 700px;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 1.5rem;
  }
`

const HeroTitle = styled.h1`
  max-width: 860px;
  font-size: clamp(3.2rem, 7vw, 6.8rem);
  font-weight: 560;
  letter-spacing: -0.06em;
  line-height: 0.95;
  margin-bottom: 1rem;
  color: #fff;

  @media screen and (max-width: 768px) {
    font-size: 2.05rem;
  }
`

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  max-width: 680px;
  margin: 0 0 2rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.9);

  @media screen and (max-width: 768px) {
    font-size: 1.15rem;
  }
`

const AnchorNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-start;
  margin: 1.5rem 0 0;
  max-width: 860px;
`

const AnchorLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.65rem 1.1rem;
  border-radius: 2px;
  background: rgba(6, 28, 49, 0.56);
  color: ${({ theme }) => theme.white};
  text-decoration: none;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.24);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.35);
  }
`

const HeroButton = styled.button`
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  font-size: 1.2rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.primaryDark};
  }
`

export const ImgContainer = styled.div`
  flex: 1;

  /* img {
    width: 100%;
    border-radius: 10px;
  } */

  @media (max-width: 768px) {
    margin-top: 2rem;
    display: none;
  }
`

const WhyGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: clamp(2rem, 6vw, 6rem);
  align-items: center;
  border-top: 1px solid rgba(16, 55, 91, 0.18);
  padding-top: clamp(2rem, 5vw, 4rem);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const WhyImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: 2px;
  border: 1px solid rgba(16, 55, 91, 0.14);
`

// Hero Component
const Hero = () => {
  return (
    <HeroSection>
      <HeroTitle>Simplifiez la gestion de vos immeubles</HeroTitle>
      <HeroSubtitle>
        Choisissez les services dont vous avez besoin et gardez une vue claire sur les demandes, les dépenses et les travaux.
      </HeroSubtitle>
      <AnchorNav aria-label="Navigation des sections principales de la page">
        <AnchorLink href="#approche">Approche humaine & locale</AnchorLink>
        <AnchorLink href="#financier">Suivi financier & rapports</AnchorLink>
        <AnchorLink href="#selection">Sélection des locataires</AnchorLink>
        <AnchorLink href="#consultation">Consultation gratuite</AnchorLink>
        <AnchorLink href="#ressources">Guides & outils gratuits</AnchorLink>
      </AnchorNav>
    </HeroSection>
  )
}

// Why Choose Us Component
const WhyChooseUs = () => (
  <Section id="approche">
    <SectionTitle>Une équipe joignable, des suivis documentés</SectionTitle>
    <SectionDescription>
      Nous accompagnons les propriétaires de Québec, de Portneuf et de la couronne ouest. Notre façon de travailler repose sur la{" "}
      <strong>transparence</strong>, le <strong>respect</strong> et des suivis faits au bon moment.
    </SectionDescription>
    <WhyGrid>
      <div>
        <h3>Information accessible</h3>
        <p>Les demandes, interventions et documents prévus au mandat sont regroupés dans des outils de suivi accessibles.</p>
        <h3>Décisions documentées</h3>
        <p>Les travaux non urgents et les dépenses importantes vous sont présentés avant d’être autorisés, selon les paramètres convenus.</p>
      </div>
      <ImgContainer>
        <WhyImage src="/img/test.jpg" alt="Équipe de gestion immobilière au travail" />
      </ImgContainer>
    </WhyGrid>
  </Section>
)

// Styled Components for Consistency
const ServiceContainer = styled.div`
  width: min(1280px, 100%);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: 0;
  margin: 0 auto;
  border-top: 1px solid rgba(16, 55, 91, 0.16);
  background: #ffffff;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ServiceImage = styled.img`
  display: block;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 480px;
  border-radius: 0;
  object-fit: cover;

  @media (max-width: 768px) {
    min-height: 280px;
    max-height: 400px;
  }
`

const ServiceContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: clamp(2.2rem, 5vw, 5rem);

  @media (max-width: 768px) {
    padding: 1rem 0.8rem;
  }
`

const ServiceHeading = styled.h3`
  font-size: clamp(2rem, 4vw, 3.8rem);
  line-height: 1.05;
  letter-spacing: -0.04em;
  margin-bottom: 1rem;
  margin-top: 0;
  color: ${({ theme }) => theme.primaryDark};
`

const ServiceText = styled.p`
  font-size: 1.05rem;
  margin-bottom: 1rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.textPrimary};
`

const ServiceList = styled.ul`
  list-style: none;
  margin-left: 0;
  padding: 0;
  margin-bottom: 1rem;
  text-align: left;
  font-size: 1.1rem;

  li {
    margin: 0;
    padding: 0.8rem 0 0.8rem 1.25rem;
    border-top: 1px solid rgba(16, 55, 91, 0.12);
    line-height: 1.6;
    position: relative;
  }

  li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 1.25rem;
    width: 7px;
    height: 7px;
    background: #337abd;
  }

  @media screen and (max-width: 768px) {
    margin-left: 0rem;
  }
`

// Services Section Component
const Services = () => (
  <Section background="#F2F2F4" $maxWidth="100%">
    <SectionTitle>Nos services</SectionTitle>
    <SectionDescription $maxWidth="1050px">
      Le mandat peut couvrir l’ensemble de la gestion ou seulement certains volets. Voici les tâches que notre équipe peut prendre en charge et
      documenter avec vous.
    </SectionDescription>

    {/* Gestion Administrative */}
    <ServiceContainer>
      <ServiceContent>
        <ServiceHeading>Gestion administrative</ServiceHeading>
        <ServiceText>
          Nous suivons les tâches récurrentes et les échéances prévues au mandat afin que l’information de chaque immeuble reste organisée. Cela peut
          comprendre :
        </ServiceText>
        <ServiceList>
          <li>Perception des loyers et suivi des paiements.</li>
          <li>Classement des factures et préparation des rapports convenus.</li>
          <li>Gestion des baux et des renouvellements selon le calendrier et les paramètres convenus.</li>
        </ServiceList>
        <ServiceText>
          Vous recevez les documents et suivis prévus au mandat afin de savoir ce qui a été fait, ce qui est en cours et ce qui demande votre
          décision.
        </ServiceText>
      </ServiceContent>
      <ServiceImage src={adminServices} alt="Documents liés à la gestion d’un immeuble" />
    </ServiceContainer>

    {/* Relations Locataires */}
    <ServiceContainer id="selection">
      <ServiceImage src={imgApartment} alt="Intérieur d’un logement résidentiel" />
      <ServiceContent>
        <ServiceHeading>Relations avec les locataires</ServiceHeading>
        <ServiceText>
          Nous utilisons des procédures simples pour recevoir les communications, conserver l’historique des demandes et diriger chaque situation vers
          la bonne personne. Le mandat peut inclure :
        </ServiceText>
        <ServiceList>
          <li>Réception et suivi des communications courantes.</li>
          <li>Documentation des plaintes et des demandes de réparation.</li>
          <li>Ligne d’urgence 7 jours sur 7 avec priorisation des situations.</li>
        </ServiceList>
        <ServiceText>
          Chaque demande est qualifiée, orientée vers la bonne personne et suivie jusqu’à sa fermeture. Lorsqu’une situation exige une décision du
          propriétaire, nous transmettons les faits et les options disponibles.
        </ServiceText>
      </ServiceContent>
    </ServiceContainer>

    {/* Maintenance et Entretien */}
    <ServiceContainer>
      <ServiceContent>
        <ServiceHeading>Entretien et travaux</ServiceHeading>
        <ServiceText>
          Un calendrier d’entretien aide à repérer les tâches à venir et à planifier les interventions. Selon le mandat, nous pouvons coordonner :
        </ServiceText>
        <ServiceList>
          <li>Les entretiens périodiques et les inspections prévues.</li>
          <li>Les demandes d’estimation et le suivi des fournisseurs retenus.</li>
          <li>Les réparations et les améliorations autorisées par le propriétaire.</li>
        </ServiceList>
        <ServiceText>Pour les travaux non urgents, une estimation et la portée de l’intervention sont présentées avant l’autorisation.</ServiceText>
      </ServiceContent>
      <ServiceImage src={maintenance} alt="Entretien d’un immeuble résidentiel" />
    </ServiceContainer>

    {/* Gestion Financière et Juridique */}
    <ServiceContainer id="financier">
      <ServiceImage src={customerService} alt="Suivi administratif d’un immeuble" />
      <ServiceContent>
        <ServiceHeading>Suivi financier et administratif</ServiceHeading>
        <ServiceText>
          Nous regroupons l’information utile au suivi de l’immeuble et appliquons les procédures administratives prévues au mandat. Nous proposons :
        </ServiceText>
        <ServiceList>
          <li>Préparation des baux et suivi des renouvellements.</li>
          <li>Préparation et suivi des règlements d’immeuble transmis aux locataires.</li>
          <li>Rapports de revenus et dépenses selon la formule retenue.</li>
          <li>Coordination avec un professionnel qualifié lorsqu’un avis juridique ou comptable est requis.</li>
        </ServiceList>
      </ServiceContent>
    </ServiceContainer>
  </Section>
)

const ResourcesSection = () => (
  <Section id="ressources">
    <SectionTitle>Guides & outils gratuits</SectionTitle>
    <SectionDescription $maxWidth="820px">
      Consultez nos listes pratiques et articles pour structurer l’entretien, les échéances et les communications liées à vos immeubles.
    </SectionDescription>
    <HeroButton as="a" href="/guide-proprietaires-portneuf">
      Découvrir les ressources
    </HeroButton>
  </Section>
)

// Regional Expertise Component
const RegionalExpertise = () => (
  <Section>
    <SectionTitle>Une présence dans votre secteur</SectionTitle>
    <SectionDescription style={{ maxWidth: "800px" }}>
      Notre équipe dessert Portneuf et la couronne ouest de Québec. Le territoire exact et les délais d’intervention sont confirmés avec chaque
      propriétaire.
    </SectionDescription>
    <GridRow col="3" $mobile>
      <div style={{ flex: 1 }}>
        <h3>Couronne ouest de Québec</h3>
        <p>Saint-Augustin-de-Desmaures, Cap-Rouge, Sainte-Foy, L’Ancienne-Lorette et les secteurs voisins convenus au mandat.</p>
      </div>

      <div style={{ flex: 1 }}>
        <h3>MRC de Portneuf</h3>
        <p>Neuville, Pont-Rouge, Donnacona, Cap-Santé, Portneuf et les municipalités voisines desservies par l’équipe.</p>
      </div>
      <div style={{ flex: 1 }}>
        <h3>Coordination locale</h3>
        <p>Réception des demandes, visites planifiées et coordination des fournisseurs selon l’emplacement et les besoins de l’immeuble.</p>
      </div>
    </GridRow>
  </Section>
)

// Styled CTA Section
const CTASection = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem 3rem;
  color: ${({ theme }) => theme.white};
  text-align: center;
  border-radius: 0;

  max-width: 100%;
  margin: 0 auto;
  overflow: hidden;

  background: url("/img/cta-background.jpg");
  background-size: cover;
  background-position: 50% 50%;
  background-repeat: no-repeat;
  /* background-blend-mode: overlay; */

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, rgba(7, 32, 55, 0.93), rgba(19, 85, 137, 0.75));
    z-index: 1;
  }
`

const CTAHeading = styled.h2`
  position: relative;
  font-size: 2.5rem;
  color: ${({ theme }) => theme.white};
  margin-bottom: 2rem;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`

const CTAButton = styled.a`
  position: relative;
  background: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.primaryDark};
  font-size: 1.2rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  font-weight: bold;
  text-decoration: none;
  transition:
    background 0.3s ease,
    transform 0.2s ease;
  z-index: 2;

  &:hover {
    background: ${({ theme }) => theme.primaryLight};
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 1.5rem;
  }
`

// CTA Component
const CTA = () => {
  return (
    <CTASection id="consultation">
      <CTAHeading>Parlons de la gestion de votre immeuble.</CTAHeading>
      <CTAButton href="/faire-une-demande">Demander une consultation</CTAButton>
    </CTASection>
  )
}

// Main Page Component
const ServicesPage = () => (
  <PageWrapper>
    <HeaderComponent />

    <main id="contenu-principal">
      <Hero />
      <WhyChooseUs />
      <Services />
      <RegionalExpertise />
      <GoogleReviews />
      <ResourcesSection />
      <CTA />
    </main>

    <Footer />
  </PageWrapper>
)

export default ServicesPage
